import { useRef, useEffect } from 'react';

/**
 * The `useLRUCache` function is a custom hook that creates a Least Recently Used (LRU) cache.
 * @param {number} size - The maximum number of items to store in the cache.
 * @returns {AppCache} - An object with `get`, `set`, and `has` methods.
 * @example
 * const cache = useLRUCache(60);
 * cache.set('foo', 'bar');
 * cache.get('foo'); // 'bar'
 * cache.has('foo'); // true
 */
export const useLRUCache = (size, initCache) => {
	// `cache` is a Map that holds the actual data.
	// using useRef to prevent re-creating the cache on every render
	const cacheRef = useRef(new Map());
	const cache = cacheRef.current;
	// `lru` is an array that holds the keys in order of use. The most recently used key is at the start of the array.
	// using useRef to prevent re-creating the lru on every render
	const lruRef = useRef([]);
	const lru = lruRef.current;

	const get = (key) => {
		if (cache.has(key)) {
			// If the key is in the cache, move it to the start of the LRU array.
			const index = lru.indexOf(key);
			lru.splice(index, 1);
			lru.unshift(key);
			return cache.get(key);
		}
		return null;
	};
	const set = (key, value) => {
		if (cache.has(key)) {
			// If the key is already in the cache, move it to the start of the LRU array.
			const index = lru.indexOf(key);
			lru.splice(index, 1);
		} else if (lru.length === size) {
			// If the cache is at maximum size, remove the least recently used item from the cache.
			const oldKey = lru.pop();
			cache.delete(oldKey);
		}
		// Add the new key to the start of the LRU array and to the cache.
		lru.unshift(key);
		cache.set(key, value);
	};
	const has = (key) => cache.has(key);
	useEffect(() => {
		initCache(set);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return { get, set, has };
};
